import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "@/lib/api";
import { track } from "@/lib/attribution";
import { useSite } from "@/context/SiteContext";

export const ROU_AVATAR = "/rou-avatar-circle.png";
export const ROU_CASH = "/c.jpeg";
export const ROU_WAVE = "/a.jpeg";
export const ROU_DOJO_BANNER = "/d.jpeg";

const INVITE = "Psst — one bill, one review, zero pressure. Ask me anything solar.";
const QUICK = ["What can solar save me?", "Does my roof qualify?", "How do batteries help?"];

function sessionId() {
  let id = null;
  try {
    id = sessionStorage.getItem("roi-session");
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
      sessionStorage.setItem("roi-session", id);
    }
  } catch (e) {
    id = `${Date.now()}-anon`;
  }
  return id;
}

export default function RoiChat() {
  const { config } = useSite();
  const [open, setOpen] = useState(false);
  const [invite, setInvite] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const controller = useRef(null);

  /** The invitation appears once per session, then Roi stays quiet. The avatar button always remains. */
  function quietInvite() {
    setInvite(false);
    try {
      sessionStorage.setItem("roi-invite-done", "1");
    } catch (e) {}
  }

  useEffect(() => {
    try {
      if (sessionStorage.getItem("roi-invite-done")) return undefined;
    } catch (e) {}
    const show = setTimeout(() => setInvite(true), 2600);
    const quiet = setTimeout(quietInvite, 16000);
    return () => {
      clearTimeout(show);
      clearTimeout(quiet);
    };
  }, []);

  useEffect(() => {
    const openRou = () => {
      quietInvite();
      setOpen(true);
    };
    window.addEventListener("open-roi", openRou);
    return () => window.removeEventListener("open-roi", openRou);
  }, []);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  async function send(text) {
    const message = (text || draft).trim();
    if (!message || busy) return;
    setDraft("");
    setMessages((m) => [...m, { role: "user", text: message }, { role: "rou", text: "", typing: true }]);
    setBusy(true);
    controller.current = new AbortController();
    try {
      const res = await fetch(`${API_URL}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, sessionId: sessionId() }),
        signal: controller.current.signal,
      });
      if (!res.ok || !res.body) throw new Error("bad status");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buffer = "";
      let errored = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += dec.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.t) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { ...last, text: (last.text || "") + parsed.t, typing: false };
                return copy;
              });
            }
            if (parsed.error) errored = parsed.error;
          } catch (e) {}
        }
      }
      if (errored) throw new Error(errored);
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "rou" && !last.text) {
          copy[copy.length - 1] = { role: "roi", text: "My circuits blinked. Try me again — or call the humans at " + config.phone + "." };
        }
        return copy;
      });
    } catch (e) {
      if (e.name === "AbortError") return;
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        copy[copy.length - 1] = { role: "roi", text: e.message?.includes("snag") || e.message?.includes("breather") ? e.message : "Roi hit a snag mid-flip. Try again, or call " + config.phone + "." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {!open && (
        <div className="roi-launch" data-testid="roi-launch">
          {invite && (
            <div className="roi-bubble" role="status" data-testid="roi-invite-bubble">
              <button type="button" className="roi-invite-dismiss" aria-label="Dismiss Roi's message" data-testid="roi-invite-dismiss" onClick={quietInvite}>
                ×
              </button>
              {INVITE}
            </div>
          )}
          <button className="roi-button" aria-label="Chat with Roi Solar, the energy ninja" data-testid="roi-chat-button" onClick={() => setOpen(true)}>
            <img src={ROU_AVATAR} alt="Roi Solar, your solar sidekick" />
          </button>
        </div>
      )}
      {open && (
        <div className="roi-panel" role="dialog" aria-label="Chat with Roi Solar" data-testid="roi-panel">
          <div className="roi-head">
            <img src={ROU_AVATAR} alt="" />
            <div>
              <b>Roi Solar</b>
              <span>Your solar sensei — upbeat, honest, zero pressure</span>
            </div>
            <button aria-label="Close chat" data-testid="roi-close" onClick={() => setOpen(false)}>×</button>
          </div>
          <div className="roi-messages" ref={listRef} data-testid="roi-messages">
            {!messages.length && (
              <div className="roi-msg roi">
                <p>Welcome to the Solar Savings Dojo! I'm Roi — I sit on piles of hypothetical savings so your actual bills don't feel lonely. Ask me anything solar.</p>
                <div className="roi-chips">
                  {QUICK.map((q) => (
                    <button key={q} data-testid="roi-quick" onClick={() => send(q)}>{q}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) =>
              m.role === "user" ? (
                <div className="roi-msg user" key={i}>{m.text}</div>
              ) : (
                <div className="roi-msg roi" key={i}>
                  {m.typing ? <span className="roi-typing"><i /><i /><i /></span> : <p>{m.text}</p>}
                </div>
              ),
            )}
          </div>
          <form
            className="roi-input"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Ask Roi anything…" maxLength={1000} data-testid="roi-input" aria-label="Message to Roi Solar" />
            <button type="submit" disabled={busy || !draft.trim()} data-testid="roi-send" aria-label="Send message">↗</button>
          </form>
          <div className="roi-handoff" style={{ padding: "0 14px" }}>
            <Link to="/contact?type=callback" data-testid="roi-handoff" onClick={() => track("chat_handoff_click")}
              style={{ display: "block", textAlign: "center", fontWeight: 800, fontSize: 12, padding: "8px 0", color: "var(--teal)" }}>
              Talk to commercial sales — request a callback ↗
            </Link>
          </div>
          <p className="roi-legal">Roi keeps it honest: no guaranteed numbers, no tax advice. The project team confirms everything.</p>
        </div>
      )}
    </>
  );
}
